import { EditorClasses } from '@scalar-org/guide'
import { customElements } from '@/elements'
import { visit } from 'unist-util-visit'
import type { Plugin } from 'unified'
import type { MarkdownNode } from '@/types'
import { shallowMerge } from '@/helpers/shallow-merge'

// ---------------------------------------------------------------------------

/**
 * Applies additional data to markdown nodes to allow them to rendered into scalar placeholder content
 * This will apply the same properties that editor.getHTML() renders
 */
export const customMarkdownPlugin: (options?: never) => Plugin =
  (_options) => () => {
    return (tree) => {
      visit(
        tree,
        (_node: unknown, index, parent: { children: MarkdownNode[] }) => {
          const node = _node as unknown as MarkdownNode
          // ---------------------------------------------------------------------------
          // Some nodes will require direct substitution with Scalar rendering methods

          // Replace single backslash with line break
          if (node.type === 'text' && node.value === '\\') {
            node.type = 'break'
          }

          // Need to explicitly differentiate between inline and block code
          // Standard code tags will be converted to the scalar custom code block element
          if (node.type === 'inlineCode') {
            node.data = shallowMerge(node.data, {
              hProperties: shallowMerge(node.data?.hProperties, {
                inline: 'true',
              }),
            })
          }

          // For the code block we will use the meta property to pass the title
          if (node.type === 'code') {
            node.data = shallowMerge(node.data, {
              hName: customElements['scalar-code'].tagName,
              hProperties: shallowMerge(node.data?.hProperties, {
                dataAttrs: JSON.stringify({
                  title: node.meta,
                  lang: node.lang ?? 'plaintext',
                }),
              }),
            })
          }

          // Support both native image tags AND scalar-image directives
          // For standard markdown image tags we need to map properties and provide the render method
          // Users can also use HTML image tags which will be not be processed
          if (node.type === 'image') {
            const scalarImage = customElements['scalar-image']

            const attrs = scalarImage.attributeTransform('', {
              src: node.url,
              alt: node.alt,
              size: 'actual',
              caption: node.title || '',
            })

            if (attrs) {
              node.data = shallowMerge(node.data, {
                hName: scalarImage.tagName,
                hProperties: shallowMerge(node.data?.hProperties, {
                  dataAttrs: JSON.stringify(attrs),
                  dataScalarDirective: 'true',
                }),
              })
            }
          }
          // ---------------------------------------------------------------------------
          // Extensions that do not have a 1:1 association in markdown instead have custom directives
          if (
            node.type === 'containerDirective' ||
            node.type === 'leafDirective' ||
            node.type === 'textDirective'
          ) {
            if (!node.name?.startsWith('scalar-')) return

            const attributes = node.attributes || {} // Attributes from the directive

            // TODO: check if multiple children are possible
            const content = node.children?.[0]?.value // Get the text content of the directive block

            // Most directives rely on a custom vueNodeRenderer and must assign placeholder tags
            // identically to the  editor.getHTML() function
            const directive =
              customElements[node.name as keyof typeof customElements]
            if (directive) {
              const attrs = directive.attributeTransform(
                content || '',
                attributes,
              )
              if (attrs) {
                // We use the placeholder element tag similar to the tiptap HTML output.
                // These placeholders are fully rendered in the SSG pipeline
                node.data = shallowMerge(node.data, {
                  hName: directive.tagName,
                  hProperties: shallowMerge(node.data?.hProperties, {
                    dataAttrs: JSON.stringify(attrs),
                    dataScalarDirective: 'true',
                  }),
                })
              } else {
                console.warn(
                  'Invalid directive attrs for',
                  node.name,
                  attributes,
                )

                // Delete the node from the tree

                if (typeof index === 'number') {
                  parent.children.splice(index, 1)
                }
              }
            }

            // Some directives we will just wrap with a standard tag
            if (node.name === 'scalar-fineprint') {
              node.data = shallowMerge(node.data, {
                hName: 'cite',
                hProperties: shallowMerge(node.data?.hProperties, {
                  class: EditorClasses.FinePrint,
                }),
              })
            }
          }
        },
      )
    }
  }
